import { BudgetMember } from '@/services/types/budget-members.types';
import {
  ACMemberEntry,
  AccountingCenter,
  AccountingCenterMemberFormValues,
  MemberAllocationItem
} from '@/services/types/accounting-centers-types';

export interface ACDefaultPool {
  acmev: number;
  acrt: number;
}

export interface ACFormMaxValues extends ACDefaultPool {}

/** Resolves the default AC for a type (same priority as the add page). */
export const resolveDefaultAccountingCenter = (
  accountingCenters: AccountingCenter[] = [],
  preferred?: Partial<AccountingCenter> | null
): AccountingCenter | undefined =>
  (preferred as AccountingCenter | undefined) ??
  accountingCenters.find((ac) => ac.is_default === true) ??
  accountingCenters[0];

/** Remaining ACMEV/ACRT pool on the default AC — same source used by the add page. */
export const getDefaultACPool = (
  defaultAC: AccountingCenter | undefined,
  budget?: { bmev?: number; bmev_used?: number; btrt?: number; btrt_achieved?: number }
): ACDefaultPool => {
  if (defaultAC) {
    return {
      acmev: defaultAC.acmev ?? 0,
      acrt: defaultAC.acrt ?? 0
    };
  }

  return {
    acmev: Math.max(0, (budget?.bmev ?? 0) - (budget?.bmev_used ?? 0)),
    acrt: Math.max(0, (budget?.btrt ?? 0) - (budget?.btrt_achieved ?? 0))
  };
};

/**
 * Max ACMEV/ACRT for form sliders.
 * - Create: default pool only (same as add page).
 * - Edit (non-default): current allocation + default pool (per API pool rules).
 */
export const getACFormMaxValues = (
  pool: ACDefaultPool,
  editingAC?: (Pick<AccountingCenter, 'acmev' | 'acrt'> & { is_default?: boolean }) | null
): ACFormMaxValues => {
  if (!editingAC || editingAC.is_default) {
    return pool;
  }

  const currentAcmev = editingAC.acmev ?? 0;
  const currentAcrt = editingAC.acrt ?? 0;

  return {
    acmev: currentAcmev + pool.acmev,
    acrt: currentAcrt + pool.acrt
  };
};

/**
 * Maps active budget members into AC creation form rows.
 * `maxOreHours` is each member's remaining ore_hours on the type's default AC.
 */
export const mapBudgetMembersToACCreationMembers = (
  budgetMembers: BudgetMember[],
  defaultAcMembers: ACMemberEntry[] = []
): AccountingCenterMemberFormValues[] => {
  const availableHoursByMemberId = new Map(defaultAcMembers.map((member) => [member.member_id, member.ore_hours]));

  return budgetMembers.map((member) => ({
    memberId: member._id,
    userId: member.user_id,
    email: member.email,
    memberName: member.name,
    profilePicUrl: member.profile_pic_url,
    type: member.type ? member.type.toLowerCase() : '',
    oreHours: 0,
    hourlyRate: member.hourly_rate,
    maxOreHours: availableHoursByMemberId.get(member._id) ?? 0
  }));
};

/**
 * Maps active budget members into AC edit form rows.
 * `oreHours` is the member's current allocation on this AC.
 * `maxOreHours` = current allocation + remaining pool on the default AC.
 */
export const mapBudgetMembersToACEditMembers = (
  budgetMembers: BudgetMember[],
  acMembers: ACMemberEntry[] = [],
  defaultAcMembers: ACMemberEntry[] = []
): AccountingCenterMemberFormValues[] => {
  const acMemberById = new Map(acMembers.map((member) => [member.member_id, member]));
  const defaultPoolByMemberId = new Map(defaultAcMembers.map((member) => [member.member_id, member.ore_hours]));

  return budgetMembers.map((member) => {
    const acMember = acMemberById.get(member._id);
    const currentOreHours = acMember?.ore_hours ?? 0;
    const defaultPool = defaultPoolByMemberId.get(member._id) ?? 0;

    return {
      memberId: member._id,
      userId: member.user_id,
      email: member.email,
      memberName: member.name,
      profilePicUrl: acMember?.profile_pic_url ?? member.profile_pic_url,
      type: member.type ? member.type.toLowerCase() : '',
      oreHours: currentOreHours,
      hourlyRate: acMember?.hourly_rate ?? member.hourly_rate,
      maxOreHours: currentOreHours + defaultPool
    };
  });
};

/** Returns only member rows whose ore hours changed (includes 0 to remove from AC). */
export const getChangedACMemberAllocations = (
  currentMembers: AccountingCenterMemberFormValues[],
  initialMembers: AccountingCenterMemberFormValues[]
): MemberAllocationItem[] => {
  const initialByMemberId = new Map(initialMembers.map((member) => [member.memberId, member]));

  return currentMembers
    .filter((member) => member.memberId)
    .filter((member) => {
      const initial = initialByMemberId.get(member.memberId);
      return Number(member.oreHours) !== Number(initial?.oreHours ?? 0);
    })
    .map((member) => ({
      member_id: member.memberId!,
      ore_hours: Number(member.oreHours)
    }));
};

export const formatAccountingCenterMemberType = (type: string): string => {
  if (!type) {
    return '';
  }

  const normalized = type.toLowerCase();
  if (normalized === 'internal_employee') {
    return 'Internal Employee';
  }
  if (normalized === 'external_collaborator') {
    return 'External Collaborator';
  }

  return type
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};
